import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { TimeRangePreset } from '@/components/TimeRangeInput';

/**
 * v0.4後半 月次実績一括入力 第2フェーズ I-4b (§6-1): 新設の一括解決API
 * `GET /work-records/shift-pattern-presets?node_id=` を使い、指定ノードで解決済みの予定パターンを
 * 1回のリクエストで取得する。旧 `useShiftPatternPresets` (パターン数分 `/settings/shift-patterns/:id/times`
 * を叩く N+1 実装) は他画面 (単票の勤務実績記録) 向けに残し、月次入力の「1日分のシート」は日ごとに
 * 勤務店舗が変わりうる (D-993 含む) ため、こちらの専用フックを使う。
 */
type PresetItem = {
  pattern_id: string; code: string; label: string; color: string | null;
  start_time: string; end_time: string; crosses_midnight: boolean; break_minutes: number;
};

export const useShiftPatternPresetsForNode = (
  nodeId: string | null | undefined, enabled: boolean,
): { presets: TimeRangePreset[]; isLoading: boolean } => {
  const { data, isLoading } = useQuery<{ patterns: PresetItem[] }>({
    queryKey: ['shift-pattern-presets-for-node', nodeId],
    queryFn: () => apiFetch<{ patterns: PresetItem[] }>(`/work-records/shift-pattern-presets?node_id=${encodeURIComponent(nodeId!)}`),
    enabled: enabled && !!nodeId,
  });

  const presets = useMemo<TimeRangePreset[]>(() => (data?.patterns ?? []).map((p) => ({
    id: p.pattern_id, label: p.label, startTime: p.start_time, endTime: p.end_time, breakMinutes: p.break_minutes,
  })), [data]);

  return { presets, isLoading };
};
